<template>
  <FullScreenSlider
    class="h-100"
    :slides="2"
    @dismiss="dismiss"
    ref="sliderRef"
    :accent-dots-index="[1]"
    dots-accent-color="#888888"
    dots-base-color="#11111138"
  >
    <Slide class="slide" id="slide-1">
      <div class="d-flex flex-column mt-4 flex-fill align-content-end">
        <div class="slide-text px-3">
          <h1>{{ getText("SLIDE_1_TITLE") }}</h1>
          <h2>{{ getText("SLIDE_1_SUBTITLE") }}</h2>
        </div>
        <div class="h-100 w-100">
          <img
            class="position-relative start-0"
            style="bottom: -6rem"
            :src="useAsset('img/credit-line/pacman-onboarding1.svg')"
            alt="pacman-slide1"
          />
        </div>
      </div>
      <Button
        class="continue-btn position-absolute end-0 start-0"
        @click="nextSlide"
      >
        {{ getText("CONTINUE") }}
      </Button>
    </Slide>

    <Slide class="slide" id="slide-2">
      <div class="d-flex flex-column mt-4">
        <div class="slide-text px-3">
          <h1>{{ getText("SLIDE_2_TITLE") }}</h1>
          <h2 class="mb-3">{{ getText("SLIDE_2_SUBTITLE") }}</h2>
        </div>
        <div>
          <img
            :src="useAsset('img/credit-line/pacman-onboarding2.svg')"
            alt="pacman-slide2"
          />
        </div>
      </div>
      <Button
        class="continue-btn position-absolute end-0 start-0"
        @click="onBtnClick"
      >
        {{ getText(hasCardOrShipment ? "SEE_CARD" : "BUTTON_TEXT") }}
      </Button>
    </Slide>
  </FullScreenSlider>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "@/apps/credit-line/composables/useI18n";

import FullScreenSlider from "@/apps/credit-line/components/FullScreenSlider.vue";
import { Slide } from "vue3-carousel";
import Button from "@/apps/credit-line/components/Button.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";

declare const Android: AndroidInterface;

const { t } = useI18n();
const router = useRouter();
const { summary, fetchSummary, isLoading } = useCreditLineSummary();

const sliderRef = ref();

const hasCardOrShipment = computed(
  () =>
    summary.value?.cards?.some((card) => card.type === "PHYSICAL") ||
    (summary.value?.shipments?.length ?? 0) !== 0
);

onMounted(async () => {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.JointLoanLineEducationalSliderLoaded,
    "PACMAN_ONBOARDING"
  );

  await fetchSummary();
});

watch(isLoading, (value) => {
  if (!value && isAndroid()) {
    Android.finishedLoading();
  }
});

function getText(name: string) {
  return t(`PACMAN_ONBOARDING.${name}`);
}

function nextSlide() {
  sliderRef.value.nextSlide();
}

function dismiss() {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine.JointLoanLineEducationalBannerDismissed,
    "PACMAN_ONBOARDING"
  );
  if (isAndroid()) {
    Android.creditOfferAccepted();
  }
}

function onBtnClick() {
  logAnalyticsEvent(
    AnalyticEvents.CreditLine
      .JointLoanLineEducationalBannerShipmentButtonPressed,
    "PACMAN_ONBOARDING"
  );
  router.replace({
    name: hasCardOrShipment.value
      ? "CreditCardView"
      : "CreditCardShippingAddressView",
  });
}
</script>

<style lang="scss" scoped>
#slide-1 {
  background: #007c3b;
  h1 {
    color: white;
    text-align: center;
    font-feature-settings: "liga" off, "clig" off;
    font-family: Inter, serif;
    font-size: 1.5rem;
    font-weight: 700;
  }

  h2:first-of-type {
    color: white;
    text-align: center;
    font-feature-settings: "liga" off, "clig" off;
    font-family: Inter, serif;
    font-size: 1.25rem;
    font-weight: 500;
  }
}

#slide-2 {
  h1 {
    color: #595a5a;
    text-align: center;
    font-feature-settings: "liga" off, "clig" off;
    font-family: Inter, serif;
    font-size: 1.5rem;
    font-weight: 700;
  }

  h2 {
    color: #595a5a;
    text-align: center;
    font-feature-settings: "liga" off, "clig" off;
    font-family: Inter, serif;
    font-size: 1.25rem;
    font-weight: 500;
  }
}

.slide {
  min-height: 100%;
  height: 100%;
  width: 100%;
  padding: 20% 0 27% 0;
  overflow: hidden;
}

.continue-btn {
  border-radius: 0.3125rem;
  width: 90%;
  height: 3rem;
  bottom: 5%;
  padding: 0.625rem 1.5rem;
  background: #8bc53f;
  margin: auto auto 0;
}
</style>
